import { createSelector } from 'reselect';

import { countriesCode } from 'constants/countries';

import * as accountCardsSelectors from 'dux/accountCards/selectors';
import * as checkoutAddressesSelectors from 'dux/checkoutAddresses/selectors';
import * as checkoutCartSelectors from 'dux/checkoutCart/selectors';
import * as checkoutPaymentSelectors from 'dux/checkoutPayment/selectors';
import * as featureFlagsSelectors from 'dux/featureFlags/selectors';
import * as userSelectors from 'dux/user/selectors';
import * as flagVariants from 'dux/featureFlags/constants/flagVariants';

export const getCheckoutStatusWithSavedPayments = createSelector(
  checkoutAddressesSelectors.getAddressesStatus,
  checkoutCartSelectors.getCartStatus,
  accountCardsSelectors.isLoading,
  (addressesStatus, cartStatus, cardsIsLoading) => {
    const statuses = [addressesStatus, cartStatus, cardsIsLoading ? 'loading' : 'success'];

    if (statuses.includes('error')) {
      return 'error';
    }

    if (statuses.includes('loading')) {
      return 'loading';
    }

    if (statuses.every(status => status === 'success')) {
      return 'success';
    }

    return 'idle';
  }
);

export const getNextStepFromStepProp = createSelector(
  checkoutCartSelectors.getIsFree,
  checkoutPaymentSelectors.getStripeCard,
  (_state, props = {}) => props.step,
  (isFree, card, step) => {
    switch (step) {
      case 'contact-address':
        if (isFree || card) {
          return 'confirmation';
        }
        return 'payment';
      case 'payment':
        return 'confirmation';
      case 'confirmation':
        return 'success';
      default:
        return null;
    }
  }
);

export const getCTAButtonLabelFromStepProp = createSelector(getNextStepFromStepProp, nextStep => {
  switch (nextStep) {
    case 'payment':
      return 'Proceed to payment';
    case 'confirmation':
      return 'Review my order';
    case 'success':
      return 'Place my order';
    default:
      return 'Next';
  }
});

export const getSelectedAddress = createSelector(
  checkoutAddressesSelectors.getAddressesData,
  checkoutCartSelectors.getCartData,
  (addresses = [], cart) => addresses?.find(({ pubkey }) => pubkey === cart.shipping_address)
);

export const getSelectedCard = createSelector(
  (state, { cardPubkey }) => accountCardsSelectors.getCardById(state, { id: cardPubkey }),
  checkoutPaymentSelectors.getStripeCard,
  (registeredCard, stripeCard) => registeredCard || stripeCard
);

export const getCTAButtonIsLoadingForCheckoutTicketFromIsSubmittingProp = createSelector(
  (_state, { isSubmitting }) => isSubmitting,
  checkoutPaymentSelectors.getIsPaymentReady,
  checkoutCartSelectors.getIsFree,
  (isSubmitting, isPaymentReady, isFreeOrder) => isSubmitting || (!isPaymentReady && !isFreeOrder)
);

export const getCTAButtonIsLoadingForCheckoutPaymentFromIsSubmittingAndCardPubkeyProp = createSelector(
  (_state, { isSubmitting }) => isSubmitting,
  (_state, { cardPubkey }) => cardPubkey,
  checkoutPaymentSelectors.getIsPaymentReady,
  (isSubmitting, cardPubkey, isPaymentReady) =>
    isSubmitting || (!isPaymentReady && cardPubkey === 'newCard')
);

export const isFreeCaShippingInTestVariant = createSelector(
  getSelectedAddress,
  userSelectors.getUserGeolocationGuessedCountry,
  featureFlagsSelectors.isFlagsDoneFetching,
  featureFlagsSelectors.getFreeCaShippingFlagVariant,
  (selectedAddress, userGeolocationGuessedCountry, flagsDoneFetching, variant) => {
    if (
      flagsDoneFetching &&
      variant === flagVariants.VARIANT1 &&
      (selectedAddress?.country === countriesCode.CA ||
        userGeolocationGuessedCountry === countriesCode.CA)
    ) {
      return true;
    }
    return false;
  }
);

export const getPricesAreDisplayedInUSDForCanadianCustomer = createSelector(
  getSelectedAddress,
  userSelectors.getUserGeolocationGuessedCountry,
  checkoutCartSelectors.getConsultationProfileCountryInCartSurvey,
  (selectedAddress, userGeolocationGuessedCountry, consultationProfileCountry) => {
    if (
      selectedAddress?.country === countriesCode.CA ||
      userGeolocationGuessedCountry === countriesCode.CA ||
      consultationProfileCountry === countriesCode.CA
    ) {
      return true;
    }
    return false;
  }
);
